import type { ScopeNode } from "@/components/ChannelMenu"

export type Grain = "month" | "week" | "day"
export type RowKind = "channels" | "products"
export type Preset = "this_month" | "last_3_months" | "this_year" | "custom"
export type SortDir = "asc" | "desc"

export interface Filters {
  preset: Preset
  from: string
  to: string
  grain: Grain
  rows: RowKind
  /** Table sort column: a period key, "total", "name", or null for the default order. */
  sort: string | null
  dir: SortDir
  products: string[]
}

export interface Cell {
  revenue: number
  quantity: number
  /** Same figure in the page's display currency (all-markets page only); null when no rate exists. */
  display?: number | null
}

export interface PivotColumn {
  key: string
  label: string
  /** The bucket extends outside the selected range (e.g. the current month to date). */
  partial: boolean
}

/** Mirrors RevenueController::MAX_PICKED_PRODUCTS. */
export const MAX_PICKED_PRODUCTS = 50

export interface PivotRow {
  key: string
  label: string
  sublabel: string | null
  slug: string | null
  cells: Record<string, Cell>
  total: Cell
}

export interface Pivot {
  columns: PivotColumn[]
  rows: PivotRow[]
  totals: Record<string, Cell>
  grand_total: Cell
  auto_products: boolean
}

export interface Scope {
  id: string
  name: string
  slug: string
}

/** One market on the all-markets page: its own native-currency pivot. */
export interface MarketSection {
  id: string
  name: string
  slug: string
  currency: string | null
  /** False when this market's figures cannot be shown in the display currency (no rate yet). */
  converted: boolean
  pivot: Pivot
}

export interface OverviewPageProps {
  filters: Filters
  markets: MarketSection[]
  currencies: string[]
  display_currency: string | null
  rate_date: string | null
  /** Units per 1 USD for each currency on the page that has a stored rate. */
  rates: Record<string, number>
}

export interface RevenuePageProps {
  filters: Filters
  pivot: Pivot
  scope: Scope
  scope_tree: ScopeNode[]
  currency: string | null
}

export const GRAIN_LABEL: Record<Grain, string> = { month: "month", week: "week", day: "day" }

export { SERIES_SLOTS, seriesColor } from "@/lib/seriesColor"
